package com.evoapps.evoshift.domain.usecase.shift

import com.evoapps.evoshift.domain.model.ClockFlag
import com.evoapps.evoshift.domain.model.ClockReviewAction
import com.evoapps.evoshift.domain.model.ClockReviewRules
import com.evoapps.evoshift.domain.model.ClockReviewSettings
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.DayClosedException
import com.evoapps.evoshift.domain.model.NotFoundException
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.UnplannedClock
import com.evoapps.evoshift.domain.model.UnplannedKind
import com.evoapps.evoshift.domain.model.needsClockReview
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import com.evoapps.evoshift.domain.usecase.NoPayRatesException
import com.evoapps.evoshift.domain.usecase.finance.CalculateDailyRecordUseCase

/**
 * SH-134: one shift on the managers' "Do sprawdzenia" (to review) list: flagged clock times the
 * last decision did not cover, or an unplanned clock-in waiting for a decision. [workerName] is who
 * clocked (for a cover: the colleague who came instead of [coveredName]).
 */
data class ClockReviewItem(
    val shiftId: String,
    val rosterId: String,
    val date: String,
    val workerId: String,
    val workerName: String,
    val coveredName: String?,
    val plannedStart: String,
    val plannedEnd: String,
    val actualStart: String?,
    val actualEnd: String?,
    /** The flags still to look at, in [ClockFlag] order. */
    val flags: List<ClockFlag>,
    val unplanned: UnplannedClock?,
    /** The tablet's clock-in photo, when the workplace takes them (managers only). */
    val photoPath: String?,
    /** Minutes clocked beyond the payable time (counting from the plan): what approving would add. */
    val extraMinutes: Long,
    /** SH-180 ([ClockFlag.CLOCK_DRIFT]): the larger distance of the self-reported times from the server's, in minutes. */
    val driftMinutes: Int? = null
) {
    val isUnplanned: Boolean get() = unplanned?.isPending == true
    val isExtraShift: Boolean get() = unplanned?.kind == UnplannedKind.EXTRA

    /** Adjusting to the plan needs a plan with length (an extra shift has none). */
    val canAdjustToPlan: Boolean get() = !isExtraShift && actualStart != null && plannedEnd > plannedStart
}

/**
 * SH-134: the "to review" list of [shifts] (e.g. a day's, or today's), oldest first. [nameOf] gives a
 * person's name for an id (the coverer's and the covered person's names come with the clock-in).
 */
fun clockReviewItems(shifts: List<Shift>, settings: ClockReviewSettings, nameOf: (String) -> String): List<ClockReviewItem> =
    shifts.asSequence()
        .filter { it.needsClockReview }
        .mapNotNull { shift ->
            val unplanned = shift.unplanned?.takeIf { it.isPending }
            val workerId = unplanned?.userId ?: shift.actualUserId ?: shift.userId ?: return@mapNotNull null
            val reviewed = shift.clockCheck?.review?.flags.orEmpty()
            ClockReviewItem(
                shiftId = shift.id,
                rosterId = shift.rosterId,
                date = shift.date,
                workerId = workerId,
                workerName = unplanned?.userName?.takeIf { it.isNotBlank() } ?: nameOf(workerId),
                coveredName = unplanned?.takeIf { it.kind == UnplannedKind.COVER }
                    ?.let { it.coveredUserName?.takeIf { n -> n.isNotBlank() } ?: it.coveredUserId?.let(nameOf) },
                plannedStart = shift.plannedTime.start,
                plannedEnd = shift.plannedTime.end,
                actualStart = shift.actualTime?.start?.takeIf { it.isNotEmpty() },
                actualEnd = shift.actualTime?.end?.takeIf { it.isNotEmpty() },
                flags = shift.clockCheck?.flags.orEmpty().filter { it !in reviewed },
                unplanned = unplanned,
                photoPath = shift.clockCheck?.startPhotoPath,
                extraMinutes = ClockReviewRules.unpaidExtraMinutes(shift, settings),
                driftMinutes = listOfNotNull(shift.clockAudit?.start?.driftMinutes, shift.clockAudit?.end?.driftMinutes)
                    .maxByOrNull { kotlin.math.abs(it) }
            )
        }
        .sortedWith(compareBy({ it.date }, { it.actualStart ?: it.plannedStart }, { it.workerName }))
        .toList()

/**
 * SH-134: a manager decides on an item of the "to review" list: approve the clocked times, adjust
 * them to the plan, type them, or confirm / reject an unplanned clock-in. The reviewClock function
 * writes the decision with a log entry (who, when, what was flagged). Then, as when recording who
 * worked a shift (SH-128, [RecordShiftWorkerUseCase]), the shift is repriced (a confirmed cover
 * moves the cost to the person who came) and the day's record recomputed and saved, so the day's
 * and the month's labour cost follow. A closed day is final ([DayClosedException]).
 */
class ReviewClockUseCase(
    private val shiftRepository: ShiftRepository,
    private val financialRepository: FinancialRepository,
    private val calculateDailyRecordUseCase: CalculateDailyRecordUseCase,
    private val calculateShiftCost: CalculateShiftCostUseCase? = null
) {
    /** The day's record after the decision (null if it could not be recomputed) and missing pay rates. */
    data class Outcome(val removed: Boolean, val record: DailyRecord?, val missingPayRates: Boolean = false)

    suspend operator fun invoke(
        workplaceId: String,
        rosterId: String,
        shiftId: String,
        date: String,
        action: ClockReviewAction,
        start: String? = null,
        end: String? = null
    ): Result<Outcome> = runCatching {
        val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrThrow().firstOrNull { it.date == date }
        if (stored?.isLocked == true) throw DayClosedException(date)
        val removed = shiftRepository.reviewClock(shiftId, action, start, end).getOrThrow()
        // A removed shift's cost snapshot goes with it (onShiftDeleted).
        val pricing = if (removed) null else calculateShiftCost?.invoke(shiftId)
        val missingRates = pricing?.exceptionOrNull().let { it is NoPayRatesException || it is NotFoundException }
        val record = calculateDailyRecordUseCase.recompute(workplaceId, rosterId, date) { it }.getOrNull()
        record?.let { financialRepository.saveDailyRecord(it).getOrThrow() }
        Outcome(removed, record, missingRates)
    }
}
